import type { Command } from '../types';
import { beautify } from './beautify';

/**
 * The two transform commands, AS DATA (ADR-0014, docs/ARCHITECTURE.md §5.3).
 *
 * Neither `run` performs anything: it returns a `CommandResult`, and `app/runCommand.ts` is
 * the one place that turns that into a store write plus at most one toast. The toolbar
 * button and the keyboard binding are both derived from these two objects, so a shortcut
 * printed on a button cannot disagree with the shortcut that fires it.
 *
 * SHORTCUTS follow docs/DESIGN.md §6.2/§7.4 (`⌘⏎` / `⇧⌘⏎`), not the `Mod-Shift-f` of the
 * illustrative snippet in ARCHITECTURE §5.3. Both collide with a CodeMirror default —
 * `Mod-Enter` is `insertBlankLine` — and our keymap has the higher precedence, which for
 * `insertBlankLine` is a fair trade. Called out in the stage report so the keymap stage
 * confirms rather than discovers it.
 */

export const formatCommand: Command = {
  id: 'beautify.format',
  labelKey: 'action.format',
  icon: 'code',
  group: 'transform',
  variant: 'primary',
  shortcut: 'Mod-Enter',
  isEnabled: (doc) => doc.text.trim().length > 0,
  run: (ctx, doc) => beautify(ctx, doc, 'pretty'),
};

export const minifyCommand: Command = {
  id: 'beautify.minify',
  // `action.compact` — the product calls this "Compact" on screen (DESIGN §6.2) while the
  // engine and this folder call the mode `minify`. The label key is the product's word.
  labelKey: 'action.compact',
  icon: 'minify',
  group: 'transform',
  variant: 'tinted',
  // Aqua: "reshape" (ADR-0101 §1). Same bytes, different shape — nothing is lost or repaired.
  hue: 'aqua',
  shortcut: 'Mod-Shift-Enter',
  isEnabled: (doc) => doc.text.trim().length > 0,
  run: (ctx, doc) => beautify(ctx, doc, 'minify'),
};

/** What `features/registry.ts` spreads. Order is toolbar order. */
export const beautifyCommands: readonly Command[] = [formatCommand, minifyCommand];
